import api from "./api";
import i18n from "../i18n";

const STORAGE_KEY = "ukar_admin_vehicle_catalog_v1";

/**
 * Per-model entry in a brand's `models` array.
 *
 * Backend changed (phase 21-01): CarBrand.models is now an array of
 * { name, vehicleType } subdocuments instead of plain strings. `vehicleType`
 * holds the `VehicleType.key` (e.g. "sedan") and may be an empty string when
 * a model has not been assigned a body type yet.
 */
export type VehicleCatalogModelEntry = { name: string; vehicleType: string };

export type VehicleCatalogBrand = {
  name: string;
  models: VehicleCatalogModelEntry[];
};

export type VehicleCatalogPayload = {
  catalogVersion: string;
  brands: VehicleCatalogBrand[];
};

// Admin-only type: includes _id for targeted CRUD operations
export type VehicleCatalogBrandWithId = {
  _id: string;
  name: string;
  models: VehicleCatalogModelEntry[];
};

/**
 * VehicleType list entry — matches the backend `VehicleType` Mongoose model
 * (see ukar-backend/src/models/vehicleTypeModel.js).
 */
export type VehicleTypeEntry = {
  _id?: string;
  key: string;
  label: string;
  order: number;
};

/**
 * GET /admins/vehicle-catalog/brands — admin-authenticated.
 * Returns full brand documents with _id (unlike the public endpoint which strips _id).
 */
export async function adminGetBrands(): Promise<VehicleCatalogBrandWithId[]> {
  const res = await api.get<{
    status: string;
    message: string;
    data: { brands: VehicleCatalogBrandWithId[] };
  }>("/admins/vehicle-catalog/brands");
  return res.data.data.brands;
}

/**
 * POST /admins/vehicle-catalog/brands — create a new brand.
 */
export async function adminCreateBrand(
  name: string
): Promise<VehicleCatalogBrandWithId> {
  const res = await api.post<{
    status: string;
    message: string;
    data: VehicleCatalogBrandWithId;
  }>("/admins/vehicle-catalog/brands", { name });
  return res.data.data;
}

/**
 * POST /admins/vehicle-catalog/brands/:id/models — add a model to a brand
 * ($addToSet, no duplicates).
 */
export async function adminAddModel(
  brandId: string,
  model: string
): Promise<VehicleCatalogBrandWithId> {
  const res = await api.post<{
    status: string;
    message: string;
    data: VehicleCatalogBrandWithId;
  }>(`/admins/vehicle-catalog/brands/${brandId}/models`, { model });
  return res.data.data;
}

/**
 * DELETE /admins/vehicle-catalog/brands/:id — delete brand and all its models.
 */
export async function adminDeleteBrand(brandId: string): Promise<void> {
  await api.delete(`/admins/vehicle-catalog/brands/${brandId}`);
}

/**
 * DELETE /admins/vehicle-catalog/brands/:id/models/:model — remove a single
 * model from a brand. :model is the model name string (URL-encoded).
 */
export async function adminDeleteModel(
  brandId: string,
  model: string
): Promise<void> {
  await api.delete(
    `/admins/vehicle-catalog/brands/${brandId}/models/${encodeURIComponent(
      model
    )}`
  );
}

function readCache(): VehicleCatalogPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VehicleCatalogPayload;
    if (!parsed?.catalogVersion || !Array.isArray(parsed.brands)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(payload: VehicleCatalogPayload) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

/**
 * GET /general/vehicle-catalog — public; caches by catalogVersion in localStorage.
 */
export async function ensureVehicleCatalog(): Promise<VehicleCatalogPayload> {
  const cached = readCache();

  try {
    const res = await api.get<{
      status: string;
      data: VehicleCatalogPayload;
    }>("/general/vehicle-catalog");
    const data = res.data?.data;
    if (!data?.brands?.length) {
      throw new Error(i18n.t("serviceErrors.invalidVehicleCatalogResponse"));
    }
    if (!cached || cached.catalogVersion !== data.catalogVersion) {
      writeCache(data);
    }
    return data;
  } catch {
    if (cached) return cached;
    throw new Error(i18n.t("serviceErrors.vehicleCatalogOffline"));
  }
}

export function brandsToSortedNames(payload: VehicleCatalogPayload): string[] {
  return payload.brands.map((b) => b.name).sort((a, b) => a.localeCompare(b));
}

export function toModelsByBrand(
  payload: VehicleCatalogPayload
): Record<string, VehicleCatalogModelEntry[]> {
  const map: Record<string, VehicleCatalogModelEntry[]> = {};
  for (const b of payload.brands) {
    map[b.name] = b.models;
  }
  return map;
}

// ---------------------------------------------------------------------------
// Vehicle type CRUD (admin) + per-model vehicleType assignment
// Endpoints introduced by phase 21-01 backend foundation.
// ---------------------------------------------------------------------------

/**
 * GET /admins/vehicle-types — list all vehicle types sorted by order.
 */
export async function adminListVehicleTypes(): Promise<VehicleTypeEntry[]> {
  const res = await api.get<{
    status: string;
    message?: string;
    data: { vehicleTypes: VehicleTypeEntry[] };
  }>("/admins/vehicle-types");
  return res.data.data.vehicleTypes;
}

/**
 * POST /admins/vehicle-types — create a new vehicle type.
 * `key` is normalized to lowercase on the backend.
 */
export async function adminCreateVehicleType(payload: {
  key: string;
  label: string;
  order: number;
}): Promise<VehicleTypeEntry> {
  const res = await api.post<{
    status: string;
    message?: string;
    data: VehicleTypeEntry;
  }>("/admins/vehicle-types", payload);
  return res.data.data;
}

/**
 * DELETE /admins/vehicle-types/:key — delete a vehicle type.
 * Backend returns 400 with the offending brand name in the message if the
 * type is still referenced by any CarBrand.models entry (in-use guard).
 */
export async function adminDeleteVehicleType(key: string): Promise<void> {
  await api.delete(`/admins/vehicle-types/${encodeURIComponent(key)}`);
}

// ---------------------------------------------------------------------------
// Vehicle type review queue (admin)
// Vehicles flagged vehicleTypeNeedsReview=true when user manually selected type.
// ---------------------------------------------------------------------------

export type PendingVehicleReview = {
  _id: string;
  brand: string;
  model: string;
  vehicleType: string;
  plateNumber: string;
  vehicleTypeNeedsReview: boolean;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
    country_code: string;
  } | null;
  createdAt: string;
};

/**
 * GET /admins/vehicles/pending-type-review — list vehicles needing type review.
 */
export async function getPendingVehicleTypeReviews(): Promise<PendingVehicleReview[]> {
  const res = await api.get<{
    status: string;
    message: string;
    data: { vehicles: PendingVehicleReview[] };
  }>("/admins/vehicles/pending-type-review");
  return res.data.data.vehicles;
}

/**
 * PATCH /admins/vehicles/:id/confirm-vehicle-type — confirm or correct vehicleType.
 * Pass updateCatalog=true to also update the CarBrand catalog entry.
 */
export async function confirmVehicleType(
  vehicleId: string,
  vehicleType: string,
  updateCatalog: boolean
): Promise<void> {
  await api.patch(`/admins/vehicles/${vehicleId}/confirm-vehicle-type`, {
    vehicleType,
    updateCatalog,
  });
}

/**
 * PATCH /admins/brands/:id/models/:modelName/vehicle-type — assign or clear
 * the vehicleType on a specific model entry within a brand.
 * Pass an empty string to clear.
 */
export async function setModelVehicleType(
  brandId: string,
  modelName: string,
  vehicleType: string
): Promise<VehicleCatalogBrandWithId> {
  const res = await api.patch<{
    status: string;
    message?: string;
    data: VehicleCatalogBrandWithId;
  }>(
    `/admins/brands/${brandId}/models/${encodeURIComponent(modelName)}/vehicle-type`,
    { vehicleType }
  );
  return res.data.data;
}
